import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { type CatalogItemData, ContractImportEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { TabsIds } from 'pages/Project/Common/types';
import { Button, cnMixSpace, IconInfo, IconWarning, Informer, RadioGroup, Text } from 'uikit';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContractCatalogSelect } from './components';
import { useUploadContract } from './useUploadContract';

import Styles from './ContractModal.module.css';

const RESULT_MODAL_TITLE = 'Загрузка расчетного файла договора';

const getLabel = R.prop('label');

export const ContractModal = ({ projectId }: { projectId: string | undefined }) => {
    const navigate = useNavigate();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { contract: contractRules } = useAccess();
    const { projectData } = useProjectData(projectId, false);
    const [isModalActive, setIsModalActive] = useState(false);

    const contractImportItems = useMemo(
        () => [
            {
                id: ContractImportEnum.Full,
                label: 'Ценовые параметры, Прейскуранты, Прочие затраты',
            },
            {
                id: ContractImportEnum.PriceListsAndPriceParameters,
                label: 'Ценовые параметры, Прейскуранты',
            },
            {
                id: ContractImportEnum.PriceListsAndOthers,
                label: 'Прейскуранты, Прочие затраты',
            },
            {
                id: ContractImportEnum.PriceListsOnly,
                label: 'Прейскуранты',
            },
        ],
        [],
    );

    const [paramsSelectedItem, setParamsSelectedItem] = useState(() => contractImportItems[0]);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<Partial<CatalogItemData> | undefined>(undefined);

    const handleParamsOnChange = useCallback(({ value }) => {
        setParamsSelectedItem(value);
    }, []);

    const handleCatalogOnChange = useCallback((value) => {
        setSelectedCatalogItem(value);
    }, []);

    const {
        isSubmitted,
        uploadContractData,
        uploadContractStatus,
        uploadContractError,
        makeRequestHandler,
        handleCloseModalError,
        handleCreateReport,
        makeUploadContractReset,
    } = useUploadContract(projectId, paramsSelectedItem.id, selectedCatalogItem?.uniqueId);

    const contractId = uploadContractData?.uerCreateContract?.data?.id;

    useEffect(() => {
        if (uploadContractStatus !== RequestStatus.FETCHING) {
            setIsModalActive(true);
        }
    }, [uploadContractStatus]);

    const handleCloseModal = useCallback(() => {
        setIsModalActive(false);
        makeUploadContractReset(undefined);
    }, [makeUploadContractReset]);

    const [resetProjectData] = useResetStoreData({ requestId: 'getProject' });

    const analyticsData = useMemo(
        () => ({
            projectId: projectData?.uniqueId,
            contractId,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectData?.uniqueId,
        ],
    );

    const handleMoveToContract = useCallback(() => {
        handleCloseModal();
        resetProjectData({});

        if (contractId) {
            navigate(`../edit/${TabsIds.PRICE}`, { state: { contractId } });
        }

        sendGpnAnalyticsEvent({
            componentId: 'rim_get to contract button_pressed',
            ...analyticsData,
        });
    }, [handleCloseModal, resetProjectData, contractId, sendGpnAnalyticsEvent, analyticsData, navigate]);

    const handleInformerCancel = useCallback(() => {
        navigate('../');
    }, [navigate]);

    useEffect(() => {
        if (uploadContractStatus === RequestStatus.SUCCESS) {
            if (uploadContractData?.uerCreateContract?.result === true) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_contract file success uploading_notification',
                    constaeventtype: 'change',
                    component: 'ALERT',
                    ...analyticsData,
                });

                sendGpnAnalyticsEvent({
                    componentId: 'rim_contract file_saved',
                    constaeventtype: 'undefined',
                    component: 'OTHER',
                    ...analyticsData,
                });
            } else {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_contract file error uploading_notification',
                    constaeventtype: 'change',
                    component: 'ALERT',
                    ...analyticsData,
                });
            }
        }
    }, [analyticsData, sendGpnAnalyticsEvent, uploadContractData?.uerCreateContract?.result, uploadContractStatus]);

    const createReportHandlerWithSPAEvent = useCallback(() => {
        handleCreateReport();

        sendGpnAnalyticsEvent({
            componentId: 'rim_contract file error upload_report created',
            ...analyticsData,
        });
    }, [analyticsData, handleCreateReport, sendGpnAnalyticsEvent]);

    const isItemsDisabled = useMemo(
        () => !contractRules?.canCreate || !selectedCatalogItem,
        [contractRules?.canCreate, selectedCatalogItem],
    );

    return (
        <div className={Styles.contractModal}>
            {R.isNil(projectData?.contractId) || projectData?.contractId === 0 ? (
                <Informer view="filled" status="system">
                    <div className={Styles.informerText}>
                        <IconInfo size="m" className={cnMixSpace({ mR: 'm' })} />
                        <Text size="s" as="span">
                            Для начала работы необходимо указать в рамках какого справочника расценок будут загружены
                            данные по договору, и загрузить расчетный файл стоимости договора в ценовых параметрах
                            подрядчика
                        </Text>
                    </div>
                </Informer>
            ) : (
                <Informer view="bordered" status="alert">
                    <div className={Styles.wrapper}>
                        <div className={Styles.informerText}>
                            <IconWarning view="alert" size="m" className={cnMixSpace({ mR: 'm' })} />
                            <Text size="s" as="span">
                                При загрузке нового файла все данные по договору будут удалены
                            </Text>
                        </div>
                        <Button label="Отмена" view="secondary" size="s" onClick={handleInformerCancel} />
                    </div>
                </Informer>
            )}

            <div className={cnMixSpace({ mV: 'xl' })}>
                <ContractCatalogSelect
                    selectValue={selectedCatalogItem}
                    onChange={handleCatalogOnChange}
                    isDisabled={!contractRules?.canCreate}
                />
            </div>

            <div className={cnMixSpace({ mV: 'xl' })}>
                <Text size="s" className={cnMixSpace({ mB: 'm' })}>
                    Параметры для считывания из файла:
                </Text>
                <RadioGroup
                    className={Styles.radioGroup}
                    onChange={handleParamsOnChange}
                    items={contractImportItems}
                    value={paramsSelectedItem}
                    getItemLabel={getLabel}
                    disabled={isItemsDisabled}
                />
            </div>

            <FilesSelector
                accept={['.xlsx']}
                isLoading={uploadContractStatus === RequestStatus.FETCHING}
                typeName="расчетный"
                manualOperationName="загрузите"
                onFilesChange={makeRequestHandler}
                disabled={isItemsDisabled}
            />

            {uploadContractStatus === RequestStatus.SUCCESS &&
                uploadContractData?.uerCreateContract?.result === true && (
                    <UploadStatus
                        isOpened={isModalActive}
                        handleCloseModal={handleCloseModal}
                        statusText="Проверка завершена. Ошибок не обнаружено."
                        titleText={RESULT_MODAL_TITLE}
                        status="success"
                        withTitleCloseButton={false}
                    >
                        <Button label="Перейти к договору" onClick={handleMoveToContract} />
                    </UploadStatus>
                )}
            <ErrorHandlingModal
                titleText={RESULT_MODAL_TITLE}
                isSubmitted={isSubmitted}
                statusRequest={uploadContractStatus}
                dataRequest={uploadContractData?.uerCreateContract}
                errorsRequest={uploadContractError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={createReportHandlerWithSPAEvent}
            />
        </div>
    );
};

ContractModal.displayName = 'ContractModal';
